import {
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    output,
    TemplateRef,
    untracked,
    viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RaSquareIconComponent } from 'ra-ui';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaEditTooltipDirective } from '../../../../directives/ra-edit-tooltip/ra-edit-tooltip.directive';
import { MediaLookPanelService } from '../../../../services/media-look-panel.service';
import { ImageWidthFieldComponent } from '../../../image-width-field/image-width-field.component';
import { MediaFillFieldComponent } from '../../../media-fill-field/media-fill-field.component';
import { MEDIA_FILL_DEFAULT } from '../../../../../../media-fill-options';
import { ImageSize, imageSizeFromWidth, imageSizeWidth } from '../../image-card/image-size.util';
import { CAPTION_STYLES, CaptionStyle } from '../../../../../../pdf-renderer/caption-style';
import { RAIL_MENU_HOST } from '../rail-menu-host';
import { LookGlyphComponent, LookGlyphName } from '../look-glyph/look-glyph.component';

export type MediaLookKind = 'image' | 'group' | 'video';

/** Um dono por instância: é por ele que o painel sabe de qual card é o conteúdo. */
let nextOwnerId = 0;

/**
 * Botão de formato do rail das mídias, zona 1: "Foto" (imagem única), "Grade" (grupo) e
 * "Vídeo". Abre o painel da direita (MediaLookPanelService) com tamanho (ou fotos por
 * linha), enquadramento (e, com a foto inteira, a cor do fundo), número e data (ou mostrar no PDF), a legenda (estilo e, na mídia
 * única, abaixo ou ao lado) e voltar ao padrão do documento.
 *
 * Era um menu flutuante preso ao rail, que abria por cima do próprio card: a pessoa mudava a
 * foto sem vê-la. O conteúdo continua aqui, num template (`#panel`), e o painel só o desenha:
 * os valores e as escolhas seguem ligados a este card.
 *
 * A largura digitada mostra no card na hora (`preview`) e salva 400ms depois que o
 * usuário para, ou já no Enter, na saída do campo e ao fechar o painel. O resto salva no clique.
 */
@Component({
    selector: 'media-look-chip',
    templateUrl: 'media-look-chip.component.html',
    styleUrl: 'media-look-chip.component.scss',
    imports: [
        ImageWidthFieldComponent,
        LookGlyphComponent,
        MediaFillFieldComponent,
        RaEditTooltipDirective,
        RaIconComponent,
        RaSquareIconComponent,
        TranslatePipe,
    ],
})
export class MediaLookChipComponent {
    public kind = input.required<MediaLookKind>();
    /** O item do card: a prévia do PDF rola até ele quando o painel abre. */
    public itemId = input<string | null | undefined>(null);
    /** Tamanho mostrado no card (imagem e vídeo; o vídeo não tem largura livre). */
    public size = input<ImageSize>({ grid: 1, width: null });
    /** Fotos por linha (grupo). */
    public columns = input<number>(2);
    public fullImage = input(false);
    /** Cor do fundo efetiva (#rrggbb): a sobra em volta da mídia inteira. */
    public fillColor = input<string>(MEDIA_FILL_DEFAULT);
    public showNumber = input(false);
    public showDate = input(false);
    public showVideo = input(true);
    /** Estilo da legenda efetivo (card, figure, band). */
    public captionStyle = input<CaptionStyle>('band');
    /** Descrição ao lado efetiva (só foto única e vídeo). */
    public captionSide = input(false);

    /** Há algo sobrescrito no campo: mostra "Voltar ao padrão do documento". */
    public canRestore = input(false);
    public disabled = input(false);

    /** Tamanho ao vivo enquanto a largura é mexida (null = volta ao salvo). */
    public preview = output<ImageSize | null>();
    public chooseSize = output<ImageSize>();
    public chooseColumns = output<number>();
    public fullImageChange = output<boolean>();
    /** Cor do fundo ao vivo enquanto o seletor livre é arrastado (null = volta à salva). */
    public fillPreview = output<string | null>();
    public fillColorChange = output<string>();
    public showNumberChange = output<boolean>();
    public showDateChange = output<boolean>();
    public showVideoChange = output<boolean>();
    public captionStyleChange = output<CaptionStyle>();
    public captionSideChange = output<boolean>();
    public restore = output<void>();

    public readonly presets = [1, 2, 3].map(grid => ({
        grid,
        glyph: `size-${grid}` as LookGlyphName,
        labelKey: `document.edit.documentConfig.singleImageSize${grid}`,
    }));
    public readonly columnOptions = [1, 2, 3];
    public readonly captionStyles = CAPTION_STYLES.map(style => ({
        style,
        glyph: `caption-${style}` as LookGlyphName,
        labelKey: `document.edit.railLook.caption${style[0].toUpperCase()}${style.slice(1)}`,
    }));

    /** Desenho das fotos por linha (1 a 3). */
    public colsGlyph(count: number): LookGlyphName {
        return `cols-${count}` as LookGlyphName;
    }

    /** O conteúdo que o painel da direita desenha. */
    public panelTemplate = viewChild.required<TemplateRef<unknown>>('panel');

    private _panel = inject(MediaLookPanelService);
    private _host = inject(RAIL_MENU_HOST, { optional: true });
    private readonly _ownerId = `media-look-${++nextOwnerId}`;

    /** O painel está aberto com o conteúdo deste card (acende o botão do rail). */
    public isOpen = computed(() => this._panel.isOpenFor(this._ownerId));

    public width = computed(() => imageSizeWidth(this.size()));

    /**
     * Ícone do botão (e do cabeçalho do painel): a paleta, o botão de "design" de todo
     * campo no rail (tamanho, enquadramento, cor, legenda).
     */
    public icon = computed(() => 'palette');

    public labelKey = computed(
        () =>
            ({
                image: 'document.edit.railLook.photo',
                group: 'document.edit.railLook.grid',
                video: 'document.edit.railLook.video',
            })[this.kind()]
    );

    private _pending: ImageSize | null = null;
    private _timer: ReturnType<typeof setTimeout> | undefined;

    constructor() {
        // Fechou o painel (✕, outro card, a prévia voltou): o que estava ao vivo salva já.
        effect(() => {
            if (this.isOpen()) return;
            untracked(() => this.flush());
        });

        // Com o painel aberto para este card, o rail dele fica à vista: é o card sendo
        // ajustado, e o botão aceso mostra de quem é o painel. Não conta como menu aberto
        // (railMenusOpen): isso esconderia o rail dos outros cards no hover, e é pelo botão
        // "Foto"/"Vídeo" deles que a pessoa troca o painel de card.
        effect(onCleanup => {
            if (!this.isOpen() || !this._host) return;
            const host = this._host;
            untracked(() => host.lookPanelOpen.set(true));
            onCleanup(() => host.lookPanelOpen.set(false));
        });

        inject(DestroyRef).onDestroy(() => {
            // Aqui os outputs já estão desligados (o Angular solta os listeners antes dos
            // destroy hooks): emitir não chegaria a ninguém. A largura ao vivo que faltava
            // salvar quem salva é o card (ver o destroy do image-card), que recebeu o preview.
            this._cancel();
            this._pending = null;
            // O card saiu da tela (outra seção, campo removido): o painel dele fecha, a menos
            // que outro card o assuma em seguida (MediaLookPanelService.release).
            this._panel.release(this._ownerId);
        });
    }

    /** O botão do rail abre o painel com este card, ou fecha se já é dele. */
    public toggle(): void {
        if (this.isOpen()) {
            this._panel.close(this._ownerId);
            return;
        }

        this.open();
    }

    /**
     * Abre o painel com este card (já aberto para ele, fica como está). É o que a prévia
     * do PDF chama pelo "Configurações deste item": o mesmo painel do botão do rail, que
     * entra por cima da prévia e a devolve ao fechar.
     */
    public open(): void {
        if (this.disabled() || this.isOpen()) return;

        this._panel.open({
            ownerId: this._ownerId,
            itemId: this.itemId(),
            titleKey: this.labelKey(),
            icon: this.icon(),
            template: this.panelTemplate(),
        });
    }

    public selectPreset(grid: number): void {
        this._cancel();
        this._pending = null;
        this.chooseSize.emit({ grid, width: null });
        this.preview.emit(null);
    }

    public onWidth(width: number): void {
        const size = imageSizeFromWidth(width);
        this._pending = size;
        this.preview.emit(size);
        this._cancel();
        this._timer = setTimeout(() => this.flush(), 400);
    }

    /** Salva a largura ao vivo (se houver) e devolve o card ao valor salvo. */
    public flush(): void {
        this._cancel();
        const size = this._pending;
        if (!size) return;
        this._pending = null;
        this.chooseSize.emit(size);
        this.preview.emit(null);
    }

    /** Cor do fundo escolhida: salva, e o card deixa a cor ao vivo. */
    public chooseFill(color: string): void {
        this.fillColorChange.emit(color);
        this.fillPreview.emit(null);
    }

    public onRestore(): void {
        this._cancel();
        this._pending = null;
        this.preview.emit(null);
        this.fillPreview.emit(null);
        this.restore.emit();
    }

    private _cancel(): void {
        if (this._timer) clearTimeout(this._timer);
        this._timer = undefined;
    }
}
